import { zodResolver } from '@hookform/resolvers/zod';
import TextField from '@mui/material/TextField';
import { useForm, useWatch } from 'react-hook-form';

import { Field } from '@/components/ui/Field';

import { detailsSchema, formatCount, type DetailsValues } from './arcSchemas';
import type { Arc } from './arcTypes';
import { FormDialog } from './FormDialog';

interface EditDetailsDialogProps {
  arc: Arc;
  /** Sets the name and description in the working copy; Save Draft saves them. */
  onApply: (title: string, description: string) => void;
  onClose: () => void;
}

/** The ARC's name and its one-line description, shown in its header in the app. */
export function EditDetailsDialog({ arc, onApply, onClose }: EditDetailsDialogProps) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<DetailsValues>({
    resolver: zodResolver(detailsSchema(arc.limits)),
    defaultValues: { title: arc.title, description: arc.description ?? '' },
  });
  const most = arc.limits.description_max_length;

  // Counted as typed, so the limit is seen before Apply refuses it (B16).
  const typed = useWatch({ control, name: 'description' }).length;

  const submit = handleSubmit(({ title, description }) => {
    if (isDirty) onApply(title, description);
    onClose();
  });

  return (
    <FormDialog
      open
      title="Edit ARC details"
      description="Shown at the top of the ARC in the app."
      dirty={isDirty}
      pending={false}
      failure={null}
      submitLabel="Apply"
      onSubmit={(event) => void submit(event)}
      onClose={onClose}
      testId="arc-builder-details-dialog"
    >
      <Field
        label="Name"
        hint="Up to 200 characters"
        error={errors.title?.message}
        testId="arc-builder-details-dialog-title"
      >
        <TextField {...register('title')} size="small" fullWidth />
      </Field>
      <Field
        label="Description"
        hint={`${formatCount(typed)} of ${formatCount(most)} characters`}
        error={errors.description?.message}
        testId="arc-builder-details-dialog-description"
      >
        <TextField
          {...register('description')}
          size="small"
          fullWidth
          multiline
          minRows={2}
        />
      </Field>
    </FormDialog>
  );
}
